import type { ContentRef } from '@gitbook/api-client';
import { assertIsDefined, assertNever } from '@gitbook/util-ts';

/**
 * Compare two references.
 * It returns true if both are equal.
 */
export function contentReferencesAreEqual(
    r1: ContentRef | null | undefined,
    r2: ContentRef | null | undefined
): boolean {
    if (r1 === null && r2 === null) {
        return true;
    }

    if (typeof r1 === 'undefined' && typeof r2 === 'undefined') {
        return true;
    }

    if (r1 && !r2) {
        return false;
    }

    if (r2 && !r1) {
        return false;
    }

    assertIsDefined(r1, 'r1 must be defined');
    assertIsDefined(r2, 'r2 must be defined');

    if (r1.kind !== r2.kind) {
        return false;
    }

    switch (r1.kind) {
        case 'space':
            return r2.kind === 'space' && r1.space === r2.space;
        case 'page':
            return r2.kind === 'page' && r1.page === r2.page && r1.space === r2.space;
        case 'anchor':
            return (
                r2.kind === 'anchor' &&
                r1.anchor === r2.anchor &&
                r1.space === r2.space &&
                r1.page === r2.page
            );
        case 'file':
            return r2.kind === 'file' && r1.file === r2.file && r1.space === r2.space;
        case 'url':
            return r2.kind === 'url' && r1.url === r2.url;
        case 'user':
            return r2.kind === 'user' && r1.user === r2.user;
        case 'reusable-content':
            return (
                r2.kind === 'reusable-content' &&
                r1.reusableContent === r2.reusableContent &&
                r1.space === r2.space
            );
        case 'openapi':
            return r2.kind === 'openapi' && r1.spec === r2.spec;
        case 'collection':
            return r2.kind === 'collection' && r1.collection === r2.collection;
        default:
            assertNever(r1);
    }

    return true;
}
